import api from './api';

const TIMEOUT = 120000; // tool-calling can take 5-30s

export const aiAgentApi = {
  me: () => api.get('/ai-agent/me').then(r => r.data),

  briefing: (account_id) =>
    api.get('/ai-agent/briefing', { params: account_id ? { account_id } : {} }).then(r => r.data),

  quickActions: (route) =>
    api.get('/ai-agent/quick-actions', { params: { route } }).then(r => r.data),

  // ----- Conversations -----
  listConversations: (limit = 100) =>
    api.get('/ai-agent/conversations', { params: { limit } }).then(r => r.data),

  createConversation: (account_id = null) =>
    api.post('/ai-agent/conversations', { account_id }).then(r => r.data),

  getConversation: (id) =>
    api.get(`/ai-agent/conversations/${id}`).then(r => r.data),

  renameConversation: (id, title) =>
    api.patch(`/ai-agent/conversations/${id}`, { title }).then(r => r.data),

  deleteConversation: (id) =>
    api.delete(`/ai-agent/conversations/${id}`).then(r => r.data),

  sendMessage: (convId, content, account_id = null) =>
    api.post(
      `/ai-agent/conversations/${convId}/messages`,
      { content, account_id },
      { timeout: TIMEOUT },
    ).then(r => r.data),

  // ----- Collaborators -----
  listCollaborators: (convId) =>
    api.get(`/ai-agent/conversations/${convId}/collaborators`).then(r => r.data),

  eligibleCollaborators: (q = '') =>
    api.get('/ai-agent/eligible-collaborators', { params: q ? { q } : {} }).then(r => r.data),

  addCollaborator: (convId, user_id) =>
    api.post(`/ai-agent/conversations/${convId}/collaborators`, { user_id }).then(r => r.data),

  removeCollaborator: (convId, userId) =>
    api.delete(`/ai-agent/conversations/${convId}/collaborators/${userId}`).then(r => r.data),

  // ----- Email -----
  listEmails: (view = 'all', { conv_id, limit = 100 } = {}) =>
    api.get('/ai-agent/email', { params: { view, conv_id, limit } }).then(r => r.data),

  getEmail: (id) =>
    api.get(`/ai-agent/email/${id}`).then(r => r.data),

  composeEmail: (payload) =>
    api.post('/ai-agent/email/compose', payload).then(r => r.data),

  approveEmail: (id) =>
    api.post(`/ai-agent/email/${id}/approve`).then(r => r.data),

  rejectEmail: (id, reason = '') =>
    api.post(`/ai-agent/email/${id}/reject`, { reason }).then(r => r.data),

  retryEmail: (id) =>
    api.post(`/ai-agent/email/${id}/retry`).then(r => r.data),

  editEmail: (id, payload) =>
    api.patch(`/ai-agent/email/${id}`, payload).then(r => r.data),

  approveBatch: ({ email_ids, batch_id } = {}) =>
    api.post('/ai-agent/email/approve-batch', { email_ids, batch_id }).then(r => r.data),

  // ----- Drive -----
  listFiles: ({ conv_id, limit = 100 } = {}) =>
    api.get('/ai-agent/drive/files', { params: { conv_id, limit } }).then(r => r.data),

  presignUpload: ({ filename, content_type, size_bytes, conv_id, visibility = 'private', org_id = null }) =>
    api.post('/ai-agent/drive/files/presign-upload', {
      filename, content_type, size_bytes, conv_id, visibility, org_id,
    }).then(r => r.data),

  confirmUpload: (payload) =>
    api.post('/ai-agent/drive/files', payload).then(r => r.data),

  downloadFile: (fileId) =>
    api.get(`/ai-agent/drive/files/${fileId}/download`).then(r => r.data),

  deleteFile: (fileId) =>
    api.delete(`/ai-agent/drive/files/${fileId}`).then(r => r.data),

  // ----- Reports -----
  getReportJob: (jobId) =>
    api.get(`/ai-agent/reports/jobs/${jobId}`).then(r => r.data),

  // ----- Admin -----
  toggleFeatureFlag: (user_id, enabled) =>
    api.post('/ai-agent/admin/feature-flag', { user_id, enabled }).then(r => r.data),

  rerollPersona: (user_id) =>
    api.post('/ai-agent/admin/persona/reroll', { user_id }).then(r => r.data),

  adminDashboard: (days = 7, org_id = null) =>
    api.get('/ai-agent/admin/dashboard', { params: { days, org_id: org_id || undefined } }).then(r => r.data),

  adminOrgs: () =>
    api.get('/ai-agent/admin/orgs').then(r => r.data),

  adminEmailQueue: ({ status, direction, org_id, limit = 200 } = {}) =>
    api.get('/ai-agent/admin/email-queue', { params: { status, direction, org_id, limit } }).then(r => r.data),

  adminJobs: ({ status, limit = 200 } = {}) =>
    api.get('/ai-agent/admin/jobs', { params: { status, limit } }).then(r => r.data),

  adminInvocations: ({ status, tool, org_id, category, query_type, limit = 200 } = {}) =>
    api.get('/ai-agent/admin/invocations', { params: { status, tool, org_id, category, query_type, limit } }).then(r => r.data),

  adminServiceHealth: (days = 7, org_id = null) =>
    api.get('/ai-agent/admin/service-health', { params: { days, org_id: org_id || undefined } }).then(r => r.data),

  // ----- Agents (Super Admin / Ops Admin) -----
  adminAgents: ({ q, org_id, limit = 200 } = {}) =>
    api.get('/ai-agent/admin/agents', { params: { q: q || undefined, org_id: org_id || undefined, limit } }).then(r => r.data),

  adminAgentConversations: (userId, { limit = 100 } = {}) =>
    api.get(`/ai-agent/admin/agents/${userId}/conversations`, { params: { limit } }).then(r => r.data),

  adminAgentConversationDetail: (userId, convId, { limit = 5, before_ts, include_tool = false } = {}) =>
    api.get(`/ai-agent/admin/agents/${userId}/conversations/${convId}`, {
      params: { limit, before_ts: before_ts || undefined, include_tool },
    }).then(r => r.data),

  adminAgentEmail: (userId, { view = 'all', limit = 100 } = {}) =>
    api.get(`/ai-agent/admin/agents/${userId}/email`, { params: { view, limit } }).then(r => r.data),

  adminAgentDrive: (userId, { limit = 200 } = {}) =>
    api.get(`/ai-agent/admin/agents/${userId}/drive`, { params: { limit } }).then(r => r.data),
};
